import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Brain,
  CheckCircle2,
  ChevronDown,
  Inbox,
  Scale,
  User,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import type { ModerationDashboardModuleDTO, ModerationMetricDTO } from '../dashboardTypes';
import {
  DashboardBusy,
  NotReadyBadge,
  moderationReadyState,
  moderationShowsValue,
  type ModerationReadyState,
} from './DashboardNotReady';
import { formatModCount } from './dashboardChartPrimitives';

function num(m: ModerationMetricDTO | undefined): number | null {
  if (m?.value == null) return null;
  const n = typeof m.value === 'number' ? m.value : Number(m.value);
  return Number.isFinite(n) ? n : null;
}

function pctOf(part: number | null, whole: number): string {
  if (part == null || whole <= 0) return '—';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

type DrillLink = { href: string; label: string };

/** Map dashboard pipeline metrics → Moderation Cases deep links. */
const PIPELINE_DRILL: Record<string, DrillLink> = {
  PIPELINE_PROCESSING: {
    href: coopQueueHref('pending'),
    label: 'Open awaiting-pipeline cases',
  },
  FRONTIER_ANALYSIS: {
    href: coopQueueHref('deep'),
    label: 'Open frontier analysis cases',
  },
  HUMAN_REVIEW: {
    href: coopQueueHref('escalated'),
    label: 'Open human review cases',
  },
  AUTO_APPROVED: {
    href: coopHistoryHref('approved'),
    label: 'Open approved decisions',
  },
  AUTO_REJECTED: {
    href: coopHistoryHref('rejected'),
    label: 'Open rejected decisions',
  },
};

function coopQueueHref(status?: string): string {
  const params = new URLSearchParams();
  params.set('view', 'queue');
  if (status) {
    params.set('coop-queue.f', JSON.stringify({ status: [status] }));
  }
  return `${ROUTES.moderation}?${params.toString()}`;
}

function coopHistoryHref(status: string): string {
  const params = new URLSearchParams();
  params.set('view', 'history');
  params.set('coop-completed.f', JSON.stringify({ status: [status] }));
  return `${ROUTES.moderation}?${params.toString()}`;
}

function availabilityLabel(state: ModerationReadyState): string {
  if (state === 'snapshot') return 'Snapshot';
  if (state === 'partial') return 'Partial';
  if (state === 'not-ready') return 'Not ready';
  return 'Live';
}

function Connector() {
  return (
    <div className="flex justify-center py-1" aria-hidden>
      <ChevronDown className="size-4 text-muted-foreground/70" />
    </div>
  );
}

function MetricDetailPopover({
  label,
  valueText,
  hint,
  metric,
  ready,
  drill,
  children,
}: {
  label: string;
  valueText: string;
  hint?: string;
  metric?: ModerationMetricDTO;
  ready: ModerationReadyState;
  drill?: DrillLink;
  children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3 border-border/50 bg-[#161618] p-4">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-white">{label}</p>
            <NotReadyBadge state={ready} {...(metric?.note ? { hint: metric.note } : {})} />
          </div>
          <p className="text-2xl font-bold tabular-nums tracking-tight text-white">{valueText}</p>
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>

        {metric?.description ? (
          <p className="text-xs leading-relaxed text-muted-foreground">{metric.description}</p>
        ) : null}

        {metric?.note ? (
          <p className="rounded-lg border border-border/40 bg-white/[0.03] px-3 py-2 text-[11px] leading-relaxed text-sky-300/90">
            {metric.note}
          </p>
        ) : null}

        <p className="text-[11px] text-muted-foreground">
          Data kind: <span className="text-foreground/90">{availabilityLabel(ready)}</span>
          {ready === 'snapshot'
            ? ' — live queue / current state, not limited to the selected date range.'
            : null}
        </p>

        {drill ? (
          <Link
            to={drill.href}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-400 hover:text-sky-300"
          >
            {drill.label}
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            No case list is linked for this stage yet.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StageRow({
  icon: Icon,
  color,
  label,
  value,
  hint,
  metric,
}: {
  icon: LucideIcon;
  color: string;
  label: string;
  value: number | null;
  hint: string;
  metric?: ModerationMetricDTO;
}) {
  const ready = moderationReadyState(metric?.availability);
  const live = moderationShowsValue(ready);
  const valueText = live ? formatModCount(value) : '—';
  const key = metric?.key ?? '';
  const drill = key ? PIPELINE_DRILL[key] : undefined;

  return (
    <MetricDetailPopover
      label={label}
      valueText={valueText}
      hint={hint}
      {...(metric ? { metric } : {})}
      ready={ready}
      {...(drill ? { drill } : {})}
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border/50 bg-[#141416] px-3 py-3 text-left',
          'transition-colors hover:border-sky-500/40 hover:bg-[#1a1a1e]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50',
        )}
        aria-label={`${label}: ${valueText}. Show details`}
      >
        <span
          className="flex size-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}22`, color }}
        >
          <Icon className="size-5" aria-hidden />
        </span>
        <p className="min-w-0 flex-1 text-sm font-medium" style={{ color }}>
          {label}
        </p>
        <div className="text-right">
          <p className="text-xl font-bold tabular-nums leading-none text-white">{valueText}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
        </div>
        <NotReadyBadge
          state={ready}
          interactive={false}
          {...(metric?.note ? { hint: metric.note } : {})}
        />
      </button>
    </MetricDetailPopover>
  );
}

function OutcomeRow({
  icon: Icon,
  color,
  label,
  value,
  pct,
  metric,
  metricKey,
}: {
  icon: LucideIcon;
  color: string;
  label: string;
  value: number | null;
  pct: string;
  metric?: ModerationMetricDTO;
  /** Override when one metric DTO is shared (e.g. penalty sources). */
  metricKey?: string;
}) {
  const ready = moderationReadyState(metric?.availability);
  const live = moderationShowsValue(ready);
  const valueText = live && value != null ? `${formatModCount(value)} (${pct})` : '—';
  const key = metricKey ?? metric?.key ?? '';
  const drill = key ? PIPELINE_DRILL[key] : undefined;

  return (
    <MetricDetailPopover
      label={label}
      valueText={valueText}
      {...(metric ? { metric } : {})}
      ready={ready}
      {...(drill ? { drill } : {})}
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left',
          'transition-colors hover:brightness-110',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50',
        )}
        style={{ backgroundColor: `${color}18` }}
        aria-label={`${label}: ${valueText}. Show details`}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md" style={{ color }}>
          <Icon className="size-4" aria-hidden />
        </span>
        <p className="min-w-0 flex-1 text-sm font-medium text-white">{label}</p>
        <p className="text-sm font-semibold tabular-nums" style={{ color }}>
          {valueText}
        </p>
        <NotReadyBadge
          state={ready}
          interactive={false}
          {...(metric?.note ? { hint: metric.note } : {})}
        />
      </button>
    </MetricDetailPopover>
  );
}

function GroupBox({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border/50 bg-[#141416] p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/** Figma vertical Moderation Pipeline: stages, routing outcomes, penalty source. */
export function ModerationPipelineFunnel({
  module,
  isLoading,
  className,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  isLoading?: boolean;
  className?: string;
}) {
  const state = moderationReadyState(module?.availability);
  const byKey = Object.fromEntries((module?.metrics ?? []).map((m) => [m.key ?? '', m]));

  const received = num(byKey.RECEIVED);
  const processing = num(byKey.PIPELINE_PROCESSING);
  const frontier = num(byKey.FRONTIER_ANALYSIS);
  const approved = num(byKey.AUTO_APPROVED);
  const rejected = num(byKey.AUTO_REJECTED);
  const human = num(byKey.HUMAN_REVIEW);
  const action = num(byKey.ACTION_PENALTY);

  const routed = (approved ?? 0) + (rejected ?? 0) + (human ?? 0);

  const sourceNote = byKey.PENALTY_SOURCE?.note ?? '';
  const noteMatch = sourceNote.match(/(\d+)\s+automated\s*\/\s*(\d+)\s+human/i);
  const autoPct = num(byKey.PENALTY_SOURCE);
  let automatedCount: number | null = null;
  let humanSourceCount: number | null = null;
  if (noteMatch) {
    automatedCount = Number(noteMatch[1]);
    humanSourceCount = Number(noteMatch[2]);
  } else if (action != null && autoPct != null) {
    automatedCount = Math.round((action * autoPct) / 100);
    humanSourceCount = Math.max(0, action - automatedCount);
  }
  const sourceTotal = (automatedCount ?? 0) + (humanSourceCount ?? 0);

  return (
    <Card className={cn('border-border/40 bg-[#1a1a1c] shadow-none', className)}>
      <CardHeader className="space-y-3 p-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h3 className="text-base font-semibold leading-6">
              {module?.label ?? 'Moderation Pipeline'}
            </h3>
            <p className="text-xs text-muted-foreground">
              How content flows through the moderation system
            </p>
          </div>
          <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
        </div>
        <p className="text-[11px] text-muted-foreground">Click a stage for definition and case links.</p>
      </CardHeader>
      <CardContent className="p-5 pt-0">
        {isLoading ? (
          <DashboardBusy className="h-64" />
        ) : state === 'not-ready' ? (
          <p className="text-sm text-muted-foreground">{module?.note ?? 'Not available yet.'}</p>
        ) : (
          <div>
            <StageRow
              icon={Inbox}
              color="#60a5fa"
              label="Received"
              value={received}
              hint="100% of total"
              metric={byKey.RECEIVED}
            />
            <Connector />
            <StageRow
              icon={Brain}
              color="#a78bfa"
              label="Pipeline processing"
              value={processing}
              hint="Live queue"
              metric={byKey.PIPELINE_PROCESSING}
            />
            <Connector />
            <StageRow
              icon={Activity}
              color="#34d399"
              label="Frontier analysis"
              value={frontier}
              hint="Live queue"
              metric={byKey.FRONTIER_ANALYSIS}
            />
            <Connector />

            <GroupBox title="Routing Outcomes">
              <OutcomeRow
                icon={CheckCircle2}
                color="#34d399"
                label="Auto Approved"
                value={approved}
                pct={pctOf(approved, routed)}
                metric={byKey.AUTO_APPROVED}
              />
              <OutcomeRow
                icon={XCircle}
                color="#f87171"
                label="Auto Rejected"
                value={rejected}
                pct={pctOf(rejected, routed)}
                metric={byKey.AUTO_REJECTED}
              />
              <OutcomeRow
                icon={User}
                color="#fbbf24"
                label="Human Review"
                value={human}
                pct={pctOf(human, routed)}
                metric={byKey.HUMAN_REVIEW}
              />
            </GroupBox>

            <Connector />
            <StageRow
              icon={Scale}
              color="#f87171"
              label="Action / Penalty"
              value={action}
              hint="Issued"
              metric={byKey.ACTION_PENALTY}
            />
            <Connector />

            <GroupBox title="Penalty Source">
              <OutcomeRow
                icon={CheckCircle2}
                color="#34d399"
                label="Automated"
                value={automatedCount}
                pct={pctOf(automatedCount, sourceTotal)}
                metric={byKey.PENALTY_SOURCE}
              />
              <OutcomeRow
                icon={User}
                color="#fbbf24"
                label="Human Review"
                value={humanSourceCount}
                pct={pctOf(humanSourceCount, sourceTotal)}
                metric={byKey.PENALTY_SOURCE}
              />
            </GroupBox>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
